import { useEffect, useState } from 'react';
import { PhoneSettingsHelp } from '@/components/PhoneSettingsHelp';
import { HomeScreenSteps } from '@/components/HomeScreenSteps';
import { alertDiagnosis, isStandalone, thisBrowser, thisDevice } from '@/lib/device';
import { systemAppNames, type AlertDiagnosis, type DeviceKind } from '@/lib/platform';

// "Did you see it?" after a test alert (Gary, 17 Oct). On his Mac, Chrome made every alert
// but macOS showed none: a Focus was on, one of Chrome's entries in Notifications was off,
// and Banners vanished after five seconds. The app cannot see those settings, so it asks,
// says what it can know for sure, then gives the fix for this device, step by step.

/** The computer's own settings, step by step. Shared with the guide (GuideScreen). */
export function ComputerAlertsHelp({ os, browser = thisBrowser }: { os: 'mac' | 'windows'; browser?: typeof thisBrowser }) {
  const names = systemAppNames(os, browser === 'other' ? 'Chrome' : browser);
  const list = (
    <>
      {names.map((n) => <span key={n}><b>{n}</b>, </span>)}
      and <b>CMB Chats</b> if you installed it
    </>
  );
  if (os === 'mac') {
    return (
      <ol className="chats-setup-howto">
        <li><b>Turn off Focus.</b> Click <b>Control Centre</b> at the top right of the screen. If <b>Focus</b> (the moon) is on, click it to turn it off.</li>
        <li><b>Allow the alerts.</b> Open <b>System Settings</b>, then <b>Notifications</b>. Find {list}. For each one, turn on <b>Allow notifications</b>.</li>
        <li><b>Make them stay.</b> On the same screen, set the style to <b>Alerts</b>. Banners vanish after a few seconds.</li>
        <li>Send another test.</li>
      </ol>
    );
  }
  return (
    <ol className="chats-setup-howto">
      <li><b>Allow the alerts.</b> Open <b>Settings</b>, <b>System</b>, <b>Notifications</b>. Turn Notifications on. Then turn on {list}.</li>
      <li><b>Turn off Do not disturb</b> on the same screen. On Windows 10, set <b>Focus assist</b> to <b>Off</b>.</li>
      <li>Send another test.</li>
    </ol>
  );
}

function diagnosisText(d: AlertDiagnosis, device: 'phone' | 'computer'): { ok: boolean; text: string } {
  // A phone browser tab, not the home-screen app (mobile review, 19 Oct): Android blocks one site in Chrome's own
  // site settings, not under Apps; an iPhone shows alerts only from the home screen.
  const tab = device === 'phone' && !isStandalone();
  switch (d) {
    case 'ok': return { ok: true, text: device === 'phone'
      ? "The app is set up correctly. Your phone's settings are hiding the alert."
      : "Your browser is set up correctly. Your computer's settings are hiding the alert." };
    case 'blocked': return { ok: false, text: device === 'phone'
      ? tab && thisDevice === 'android'
        ? 'Alerts are blocked for this site. Tap the icon left of the address bar, then Permissions (or Notifications), choose Allow, then reload this page.'
        : "Alerts are blocked on this phone. Allow CMB Chats in your phone's Settings, then send another test."
      : 'Alerts are blocked in the browser. Click the padlock beside the web address, set Notifications to Allow, then reload.' };
    case 'not_asked': return { ok: false, text: 'Alerts are not turned on yet. Turn on Notifications above, then send another test.' };
    case 'not_registered': return { ok: false, text: `Alerts are allowed, but this ${device} is not registered for them yet. Close the app, open it again, then send another test.` };
    default: return { ok: false, text: tab && thisDevice === 'iphone'
      ? 'On iPhone, alerts work only from the home screen. Add CMB Chats to your Home Screen, open it from there, then send another test.'
      : 'This browser cannot show alerts. Use Chrome or Edge on a computer, or the app on your phone.' };
  }
}

function FixSteps({ kind }: { kind: DeviceKind }) {
  if (kind === 'mac' || kind === 'windows') return <ComputerAlertsHelp os={kind} />;
  if (kind === 'iphone' || kind === 'android') {
    return (
      <>
        <PhoneSettingsHelp os={kind === 'iphone' ? 'ios' : 'android'} />
        <p className="chats-seen-note">Then send another test.</p>
      </>
    );
  }
  return (
    <ol className="chats-setup-howto">
      <li>Open your computer's notification settings.</li>
      <li>Allow alerts from <b>{thisBrowser === 'other' ? 'your browser' : thisBrowser}</b>, and turn off <b>Do not disturb</b>.</li>
      <li>Send another test.</li>
    </ol>
  );
}

/**
 * Asked once a test alert has gone. Yes closes it. No shows what the app knows, then the fix
 * for this device, with a button to test again. `failed`: the app knows no alert was shown, so it
 * goes straight to the fix and never asks (mobile review, 19 Oct).
 */
export function AlertCheck({ onRetest, failed = false }: { onRetest: () => void; failed?: boolean }) {
  const kind = thisDevice;
  const [answer, setAnswer] = useState<'yes' | 'no' | null>(failed ? 'no' : null);
  const [diagnosis, setDiagnosis] = useState<AlertDiagnosis | null>(null);
  const device = kind === 'iphone' || kind === 'android' ? 'phone' : 'computer';
  useEffect(() => { if (failed) void alertDiagnosis().then(setDiagnosis); }, [failed]);
  // Yes leaves a short line, not an empty box (mobile review, 19 Oct).
  if (answer === 'yes') return <div className="chats-seen"><p className="chats-seen-ok">Good, this {device} gets alerts.</p></div>;
  if (answer === null) {
    return (
      <div className="chats-seen">
        <p className="chats-seen-q">Did you see it?</p>
        <div className="chats-seen-actions">
          <button type="button" className="chats-btn" onClick={() => setAnswer('yes')}>Yes</button>
          <button
            type="button"
            className="chats-btn chats-btn--ghost"
            onClick={() => { setAnswer('no'); void alertDiagnosis().then(setDiagnosis); }}
          >No</button>
        </div>
      </div>
    );
  }
  const said = diagnosis ? diagnosisText(diagnosis, device) : null;
  return (
    <div className="chats-seen">
      <p className="chats-seen-q">Let's fix it</p>
      {said && <p className={said.ok ? 'chats-seen-ok' : 'chats-setup-warn'}>{said.text}</p>}
      {(!said || said.ok) && <FixSteps kind={kind} />}
      {diagnosis === 'unsupported' && kind === 'iphone' && !isStandalone() && <HomeScreenSteps />}
      <button type="button" className="chats-btn chats-step-btn" onClick={() => { setAnswer(null); onRetest(); }}>Send another test</button>
    </div>
  );
}
